import { useState } from 'react';
import { ReportFilters } from '../components/ReportFilters';
import { ReportCard } from '../components/ReportCard';
import { useDownloadReport } from '../hooks/useDownloadReport';
import { REPORTS } from '../types/report';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { ReportFilters as FilterType, ReportType } from '../types/report';

/**
 * Reports page with date filters and downloadable Excel/PDF reports
 */
export function ReportsPage() {
  usePageTitle('Reports');
  const [filters, setFilters] = useState<FilterType>({});
  const { download, openPdf, isDownloading, downloadingReport, error } = useDownloadReport();

  const getReportFilters = (reportType: ReportType) => {
    const report = REPORTS.find((r) => r.id === reportType);
    return report?.requiresDateRange ? filters : undefined;
  };

  const handleDownloadExcel = async (reportType: ReportType) => {
    await download(reportType, 'xlsx', getReportFilters(reportType));
  };

  const handleDownloadPdf = async (reportType: ReportType) => {
    await download(reportType, 'pdf', getReportFilters(reportType));
  };

  const handleOpenPdf = async (reportType: ReportType) => {
    await openPdf(reportType, getReportFilters(reportType));
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Reports</h1>
        <p className="text-muted-foreground">
          Download reports as Excel spreadsheets or PDF documents
        </p>
      </div>

      <ReportFilters filters={filters} onChange={setFilters} />

      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((report) => (
          <ReportCard
            key={report.id}
            report={report}
            filters={filters}
            onDownloadExcel={() => handleDownloadExcel(report.id)}
            onDownloadPdf={() => handleDownloadPdf(report.id)}
            onOpenPdf={() => handleOpenPdf(report.id)}
            isDownloading={isDownloading && downloadingReport === report.id}
          />
        ))}
      </div>
    </div>
  );
}
